<template>
    <div class="row">
        <div class="col-lg-7">
            <label class="form-check-label">Date</label>
            <date-picker
                v-model="date"
                :formatter="formatter"
                :format="$options._static.dateFormats.from"
                @input="updateTime"
                valueType="format"
                range
                :class="{ 'is-invalid': errors.date_from || errors.date_to }"
            >
            </date-picker>
            <span
                class="invalid-feedback"
                role="alert"
                v-if="errors.date_from || errors.date_to"
            >
                <strong>
                    <span v-if="errors.date_from"> {{ errors.date_from[0] }} </span>
                    <span v-if="errors.date_from && errors.date_to"> - </span>
                    <span v-if="errors.date_to"> {{ errors.date_to[0] }} </span>
                </strong>
            </span>
        </div>
        <div class="mt-4 mt-lg-0 col-lg-5">
            <div class="form-check form-switch">
                <label class="form-check-label">
                    <span class="position-relative">
                        Travel mode
                        <img
                            :src="'/images/icons/info.svg'"
                            class="cursor-info"
                            width="15"
                            style="position: absolute; top: -8px; right: -16px"
                            alt="fan size"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title="Travel Mode lets venues located outside your travel radius find and book on selected dates without needing to change your home location.  You can update dates and locations in your calendar."
                        />
                    </span>
                </label>
                <div class="form-check-tour mt-1">
                    <span class="me-lg-1 pt-lg-2">off</span>
                    <input
                        class="form-check-input mt-5 mt-lg-0"
                        name="tour_mode"
                        v-model="cloneEvent.tour_mode"
                        @change="changeTour()"
                        value="1"
                        type="checkbox"
                    />
                    <span class="ps-9 pt-lg-2">on</span>
                </div>
            </div>
        </div>
        <div class="col-lg-8 mt-4 mt-lg-3">
            <div class="row">
                <div class="col-lg-12">
                    <label class="form-check-label"> Availability </label>
                </div>
                <div class="col-lg-6">
                    <div class="form-check form-radio">
                        <label class="form-check-label"> Available </label>
                        <input
                            class="form-check-input"
                            type="radio"
                            :disabled="cloneEvent.tour_mode == 1"
                            v-model="cloneEvent.available"
                            value="1"
                            name="available"
                        />
                    </div>
                </div>
                <div class="col-lg-6">
                    <div class="form-check form-radio">
                        <label class="form-check-label"> Day off </label>
                        <input
                            class="form-check-input"
                            type="radio"
                            :disabled="cloneEvent.tour_mode == 1"
                            @click="cloneEvent.price = 0"
                            v-model="cloneEvent.available"
                            value="0"
                            name="available"
                        />
                    </div>
                </div>
            </div>
        </div>
        <div class="col-lg-12 mt-3" v-if="cloneEvent.available == 1">
            <div class="mb-3">
                <label class="form-label">Price</label>
                <div
                    class="custom-number-input-wrapper"
                    :class="{ 'is-invalid': errors.price }"
                >
                    <div
                        class="form-input-border input-group border-50"
                        :class="{ 'is-invalid': errors.price }"
                    >
                        <div class="col-2 d-flex justify-content-center align-items-center">
                            <span class="main-text-bold">$</span>
                        </div>
                        <div class="col-10">
                            <input
                                type="number"
                                ref="price"
                                v-model="cloneEvent.price"
                                @focus="fixFocus($event)"
                                @input="priceInputHandler"
                                name="price"
                                placeholder="Price"
                                class="w-100 form-control custom-arrows price-input group-input border-50 py-4 skip-custom-number js-price"
                            />
                        </div>
                    </div>
                </div>
                <span class="invalid-feedback" role="alert" v-if="errors.price"
                    ><span v-if="errors.price"> {{ errors.price[0] }} </span></span
                >
            </div>
        </div>
        <div class="col-lg-12 mt-3" v-if="cloneEvent.tour_mode == 1">
            <div class="mb-3">
                <div class="row">
                    <div class="col-12">
                        <label for="band_requirement" class="main-text-bold pb-3 pt-3 px-0"
                            >Band Requirements</label
                        >
                        <div class="col-sm-12">
                            <select
                                id="band_requirement"
                                class="select-requirement col-sm-12"
                                name="band_requirement[]"
                                multiple="multiple"
                            >
                                <template v-for="(req, idx) in mergedRequirements">
                                    <option
                                        :key="idx"
                                        :selected="req.selected"
                                        :value="req.id"
                                        :data-requirement-icon-url="req.iconUrl"
                                    >
                                        {{ req.description }}
                                    </option>
                                </template>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="col-lg-12 mt-3" v-if="isSoundEquipmentVisible">
            <div class="mb-2">
                <label class="main-text-bold pb-3 pt-3 px-0"
                    >What kind of equipment your band needs?</label
                >
                <div class="col-12">
                    <select
                        class="select-requested-sound-equipment custom-select chips col-12"
                        name="requested_sound_equipment[]"
                        multiple="multiple"
                    >
                        <template v-for="(equipment, idx) in requestedSoundEquipment">
                            <optgroup
                                :key="idx"
                                :label="equipment"
                                :data-select2-id="`select2-${idx + equipment}`"
                            >
                                <option
                                    :value="equipment"
                                    selected
                                    :data-select2-id="`select2-${idx + equipment}`"
                                >
                                    {{ equipment }}
                                </option>
                            </optgroup>
                        </template>
                    </select>
                </div>
            </div>
        </div>
        <div class="col-lg-12 mt-3" v-if="cloneEvent.tour_mode">
            <div class="mb-3">
                <label class="form-label">Location</label>
                <input
                    type="text"
                    v-model="cloneEvent.location"
                    placeholder="Location"
                    ref="location"
                    name="location"
                    id="address-artist"
                    :class="{ 'is-invalid': errors.location, 'is-invalid': errors.lat }"
                    class="group-input form-control border-50 py-4"
                />
                <span class="invalid-feedback" role="alert" v-if="errors.location">
                    <strong>{{ errors.location[0] }}</strong>
                </span>
                <span
                    class="invalid-feedback"
                    role="alert"
                    v-if="errors.lat && !errors.location"
                >
                    <strong>{{ errors.lat[0] }}</strong>
                </span>
            </div>
        </div>
        <div class="col-lg-12 mt-3" v-if="cloneEvent.tour_mode">
            <div class="mb-3">
                <label class="form-label">Radius</label>
                <div class="custom-number-input-wrapper">
                    <div class="form-input-border input-group border-50">
                        <div class="col-2 d-flex justify-content-center align-items-center">
                            <span class="main-text-bold">Mi</span>
                        </div>
                        <div class="col-10">
                            <input
                                type="number"
                                min="1"
                                step="1"
                                max="1000"
                                name="radius"
                                v-model="cloneEvent.radius"
                                placeholder="Radius"
                                :class="{ 'is-invalid': errors.radius }"
                                class="form-control w-100 custom-arrows radius-input group-input border-50 py-4"
                            />
                            <div class="row number-input-controls-container">
                                <div class="col-2 d-flex input-controls px-4">
                                    <div class="input-controls-wrapper row mx-3 py-3">
                                        <div
                                            ref="price-upper"
                                            class="js-number-input-upper custom-number-input-upper-arrow"
                                        >
                                            <img
                                                src="images/icons/number-input-upper.png"
                                                alt="increase value"
                                            />
                                        </div>
                                        <div
                                            ref="price-lower"
                                            class="js-number-input-lower custom-number-input-lower-arrow"
                                        >
                                            <img
                                                src="images/icons/number-input-lower.png"
                                                alt="decrease value"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <span class="invalid-feedback" role="alert" v-if="errors.radius">
                    <strong>{{ errors.radius[0] }}</strong>
                </span>
            </div>
        </div>
        <div class="col-lg-12 mt-3">
            <button type="button" class="btn custom-modal-btn-close btn-text" @click="close()">
                Close
            </button>
            <button
                type="button"
                class="btn custom-modal-btn-confirm btn-text"
                @click="create()"
                v-if="cloneEvent.create"
            >
                Save
            </button>
            <button
                type="button"
                class="btn custom-modal-btn-confirm btn-text"
                @click="update()"
                v-else
            >
                Save
            </button>
            <!--            <button class="btn btn-warning" @click="destroy()" v-if="!cloneEvent.create">Delete</button>-->
        </div>
    </div>
</template>
<script>
    import { notification } from '../../helpers/notification'
    import DatePicker from 'vue2-datepicker'
    import 'vue2-datepicker/index.css'
    import moment from 'moment'
    import { Money } from 'v-money'
    import { Tooltip } from 'bootstrap'

    const axios = require('axios')
    const Swal = require('sweetalert2')
    new Tooltip(document.body, {
        selector: "[data-bs-toggle='tooltip']"
    })
    export default {
        name: 'Event',
        props: ['event', 'bandRequirements'],
        components: { DatePicker, Money },
        data() {
            const { dateFormats } = this.$options._static

            return {
                date: [
                    moment(this.event.date_from, dateFormats.to).format(dateFormats.from),
                    moment(this.event.date_to, dateFormats.to).format(dateFormats.from)
                ],
                formatter: {
                    stringify: (date) => (date ? moment(date).format(dateFormats.from) : null),
                    parse: (string) =>
                        string ? moment(string, dateFormats.from).toDate() : null
                },
                cloneEvent: this.event,
                errors: {},
                tourBandRequirements: [],
                selectedRequirements: [],
                requestedSoundEquipment: [],
                selectedRequestedSoundEquipment: []
            }
        },
        computed: {
            mergedRequirements() {
                let selectedIds = this.tourBandRequirements.map((e) => e.id)
                return [...this.bandRequirements].map((req) => {
                    req.selected = selectedIds.includes(req.id)
                    return req
                })
            },
            isSoundEquipmentVisible() {
                if (!this.tourBandRequirements.length) return false
                return this.tourBandRequirements
                    .map((e) => (e.text ? e.text : e.description))
                    .map((e) => e.toLowerCase())
                    .some((req) => req.includes('sound') && req.includes('equipment'))
            }
        },
        methods: {
            fixFocus(event) {
                event.stopImmediatePropagation()
            },
            priceInputHandler(newVal) {
                if (this.cloneEvent.price.length > 20) {
                    this.cloneEvent.price = this.cloneEvent.price.slice(0, 20)
                }
            },
            changeTour() {
                let vm = this
                setTimeout(function () {
                    vm.initPlaceApi()
                }, 500)
                this.cloneEvent.available = 1
                this.cloneEvent.location = null
                this.cloneEvent.radius = 1
            },
            create() {
                let vm = this
                axios
                    .post('/events', this.cloneEvent)
                    .then(function (response) {
                        notification('success', response.data.message)
                        vm.$emit('create')
                    })
                    .catch((err) => {
                        if (err.response.data.errors) {
                            notification('error', err.response.data.errors.date_valid)
                            vm.errors = err.response.data.errors
                        } else if (err.response.data.message) {
                            notification('error', err.response.data.message)
                        }
                    })
            },
            update() {
                let vm = this
                this.cloneEvent._method = 'PUT'
                axios
                    .post('/events/' + this.cloneEvent.id, this.cloneEvent)
                    .then(function (response) {
                        notification('success', response.data.message)
                        vm.$emit('update')
                    })
                    .catch((err) => {
                        if (err.response.data.errors) {
                            notification('error', err.response.data.errors.date_valid)
                            vm.errors = err.response.data.errors
                        } else if (err.response.data.message) {
                            notification('error', err.response.data.message)
                        }
                    })
                    .finally(() => {})
            },
            close() {
                this.$emit('close')
            },
            confirmUpdate() {
                let url = this.cloneEvent._method
                    ? '/events/' + this.cloneEvent.id
                    : '/events/'
                let method = this.cloneEvent._method ? 'update' : 'create'
                let vm = this
                Swal.fire({
                    heightAuto: false,
                    title: 'Update Confirmation',
                    html: 'Some existing events will be changed, are you sure?',
                    showCancelButton: true,
                    confirmButtonText: 'Confirm',
                    cancelButtonText: 'Cancel'
                }).then((result) => {
                    if (result.value) {
                        vm.cloneEvent.force = true
                        axios
                            .post(url, vm.cloneEvent)
                            .then(function (response) {
                                notification('success', response.data.message)
                                vm.$emit(method)
                            })
                            .catch((err) => {
                                if (err.response.data.errors) {
                                    vm.errors = err.response.data.errors
                                } else if (err.response.data.message) {
                                    notification('error', err.response.data.message)
                                }
                            })
                            .finally(() => {})
                    }
                })
            },
            destroy() {
                let vm = this
                Swal.fire({
                    heightAuto: false,
                    title: 'Deletion Confirmation',
                    html: 'This event will be deleted. Are you sure?',
                    showCancelButton: true,
                    confirmButtonText: 'Confirm',
                    cancelButtonText: 'Cancel'
                }).then((result) => {
                    if (result.value) {
                        axios
                            .delete('/events/' + vm.cloneEvent.id)
                            .then(function (response) {
                                notification('success', response.data.message)
                                vm.$emit('delete')
                            })
                            .catch((err) => {
                                if (err.response.data.message) {
                                    notification('error', err.response.data.message)
                                }
                            })
                            .finally(() => {})
                    }
                })
            },
            updateTime([dateFrom, dateTo]) {
                const { dateFormats } = this.$options._static
                this.cloneEvent.date_from = moment(dateFrom, dateFormats.from).format(
                    dateFormats.to
                )
                this.cloneEvent.date_to = moment(dateTo, dateFormats.from).format(
                    dateFormats.to
                )
            },
            initPlaceApi() {
                let vm = this
                let placeElement = this.$refs.location
                if (placeElement) {
                    let autocomplete = new google.maps.places.Autocomplete(placeElement, {
                        types: ['(regions)'],
                        componentRestrictions: { country: 'us' }
                    })
                    autocomplete.addListener('place_changed', function () {
                        let place = autocomplete.getPlace()
                        let latitude = place.geometry.location.lat()
                        let longitude = place.geometry.location.lng()
                        vm.cloneEvent.location = place.formatted_address
                        vm.cloneEvent.lat = latitude
                        vm.cloneEvent.lon = longitude
                    })
                }
            },
            initBandRequirementsSelect() {
                this.$nextTick(() => {
                    const { _static } = this.$options
                    if (_static.bandRequirementsNode) return
                    _static.bandRequirementsNode = $('.select-requirement')
                    _static.bandRequirementsNode.select2({
                        placeholder: 'Let Venues know any requirements you have.',
                        allowClear: true,
                        templateResult(el) {
                            if (!el?.id) return el.text
                            let iconUrl = $(el.element).data('requirementIconUrl')
                            if (iconUrl)
                                return $(
                                    `<span><img class="requirement-icon" src="${iconUrl}" alt=""/>${el.text}</span>`
                                )
                            return $(`<span>${el.text}</span>`)
                        },
                        templateSelection(el) {
                            if (!el?.id) return el.text
                            let iconUrl = $(el.element).data('requirementIconUrl')
                            if (iconUrl)
                                return $(
                                    `<span><img class="requirement-icon" style="max-height: 18px" src="${iconUrl}" alt=""/>${el.text}</span>`
                                )
                            return $(`<span>${el.text}</span>`)
                        }
                    })
                    _static.bandRequirementsNode.on('change.select2', () => {
                        let data = _static.bandRequirementsNode.select2('data')
                        let ids = data.map((e) => +e.id)
                        this.tourBandRequirements = data
                        this.selectedRequirements = this.cloneEvent.band_requirement = ids
                    })
                })
            },
            destroyBandRequirementsSelect() {
                const { _static } = this.$options
                if (!_static.bandRequirementsNode) return
                _static.bandRequirementsNode.select2('destroy')
                _static.bandRequirementsNode = null
                // destroying select2 instance
            },
            initSoundEquipmentChips() {
                this.$nextTick(() => {
                    const { _static } = this.$options
                    if (_static.requestedSoundEquipmentNode) return
                    _static.requestedSoundEquipmentNode = $(
                        '.select-requested-sound-equipment'
                    )
                    _static.requestedSoundEquipmentNode.select2({
                        allowClear: true,
                        tags: true,
                        placeholder: 'What kind of equipment your band needs?'
                    })
                    _static.requestedSoundEquipmentNode.on('change.select2', () => {
                        let data = _static.requestedSoundEquipmentNode.select2('data')
                        this.selectedRequestedSoundEquipment =
                            this.cloneEvent.requested_sound_equipment = data.map((e) => e.id)
                    })
                })
            },
            destroySoundEquipmentChips() {
                const { _static } = this.$options
                if (!_static.requestedSoundEquipmentNode) return
                _static.requestedSoundEquipmentNode.select2('destroy')
                _static.requestedSoundEquipmentNode = null
            },
            initPriceInput() {
                let intervalDelay = 50,
                    timeoutDelay = 250,
                    intervalId,
                    timeoutId,
                    input = this.$refs.price.$el,
                    clear = () => !clearInterval(intervalId) && !clearTimeout(timeoutId),
                    changeValue = (input, up = true) => {
                        if (input.disabled || input.readOnly) return
                        up
                            ? this.cloneEvent.price++
                            : this.cloneEvent.price > 0 && this.cloneEvent.price--
                        clear()
                        timeoutId = setTimeout(() => {
                            intervalId = setInterval(() => {
                                up
                                    ? this.cloneEvent.price++
                                    : this.cloneEvent.price > 0 && this.cloneEvent.price--
                            }, intervalDelay)
                        }, timeoutDelay)
                    }

                let upperBtn = $(input).parent().find('.js-number-input-upper-price')
                let lowerBtn = $(input).parent().find('.js-number-input-lower-price')

                // increment value
                upperBtn.mousedown(() => changeValue(input))
                upperBtn.on('mouseleave mouseup', clear)

                // decrement value
                lowerBtn.mousedown(() => changeValue(input, false))
                lowerBtn.on('mouseleave mouseup', clear)
            }
        },
        mounted() {
            let tourId = this.cloneEvent.tour_id
            if (tourId) {
                axios
                    .get(`tours/${tourId}`)
                    .then((res) => {
                        this.tourBandRequirements = res.data.selected
                        this.requestedSoundEquipment = res.data.requested_sound_equipment
                    })
                    .then(this.initBandRequirementsSelect)
            }
            this.initPlaceApi()
            if (this.$refs?.price?.$el) this.initPriceInput()
        },
        beforeDestroy() {
            this.destroyBandRequirementsSelect()
            this.destroySoundEquipmentChips()
        },
        watch: {
            errors(newVal, oldVal) {
                if (Object.keys(newVal).length == 1 && newVal.date_valid) {
                    if (
                        newVal.date_valid[0] !==
                        'There are already has booked show for selected date.'
                    ) {
                        this.confirmUpdate()
                    }
                }
            },
            'cloneEvent.tour_mode'(enabled) {
                if (enabled) {
                    this.initBandRequirementsSelect()
                    this.$nextTick(window.__customNumberInit)
                } else this.destroyBandRequirementsSelect()
            },
            isSoundEquipmentVisible(enabled) {
                if (enabled) this.initSoundEquipmentChips()
                else this.destroySoundEquipmentChips()
            }
        },
        _static: {
            bandRequirementsNode: null,
            requestedSoundEquipmentNode: null,
            dateFormats: {
                from: 'DD MMM, YYYY',
                to: 'YYYY-MM-DD'
            }
        }
    }
</script>
<style lang="scss">
    @import '../../../sass/_variables.scss';
    .pac-container {
        z-index: 999999;
    }

    .is-invalid .mx-icon-calendar,
    .is-invalid .mx-icon-clear {
        right: 28px;
    }

    .mx-datepicker.is-invalid input {
        border-color: #dc3545 !important;
        padding-right: calc(1.6em + 0.75rem);
        background-color: #fafafa;
    }

    .mx-datepicker.is-invalid input {
        border-color: #dc3545;
        padding-right: calc(1.6em + 0.75rem);
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23dc3545'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
        background-repeat: no-repeat;
        background-position: right calc(0.4em + 0.1875rem) center;
        background-size: calc(0.8em + 0.375rem) calc(0.8em + 0.375rem);
    }

    .mx-datepicker input {
        display: block;
        width: 100%;
        padding: 0.375rem 0.75rem;
        font-size: 0.9rem;
        font-weight: 400;
        line-height: 1.6;
        color: #212529;
        background-color: #f8fafc;
        background-clip: padding-box;
        border: 1px solid #ced4da;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        border-radius: 0.25rem;
        transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    }
</style>
